import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cancelSubscription } from "@/utils/stripe";

export default function CancelSubscription({ customerId, plan, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [scheduled, setScheduled] = useState(false);
  const endDate = plan.cancelAt || plan.renewalDate;
  const formattedEndDate = endDate
    ? new Date(endDate).toLocaleDateString("en-AU", {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  if (plan.isOneTime || !["active", "trialing", "past_due"].includes(plan.status)) return null;

  const accessMessage = formattedEndDate
    ? `You can keep using your subscription until ${formattedEndDate}.`
    : "You can keep using your subscription until the end of your current billing period.";

  async function confirmCancellation() {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      await cancelSubscription(customerId, false);
    } catch (err) {
      setError(err?.message || "We couldn't cancel your subscription. Please try again.");
      setPending(false);
      return;
    }
    setScheduled(true);
    setOpen(false);
    setPending(false);
    // Refresh failure must not turn a successful cancellation into a retry.
    try { await onCancelled?.(); } catch { /* The success message remains visible. */ }
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      {scheduled || plan.cancelAtPeriodEnd ? (
        <p role="status" className="text-sm text-gray-600">
          Cancellation scheduled. {accessMessage} Your subscription will not renew.
        </p>
      ) : (
        <Dialog.Root open={open} onOpenChange={(next) => {
          if (!pending) { setOpen(next); setError(""); }
        }}>
          <Dialog.Trigger asChild>
            <button type="button" className="min-h-11 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700">
              Cancel subscription
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-5 shadow-lg">
              <Dialog.Title className="text-lg font-semibold">Cancel subscription?</Dialog.Title>
              <Dialog.Description className="mt-2 text-sm text-gray-600">
                {accessMessage} After that, your subscription will end and you will not be charged for another billing period.
              </Dialog.Description>
              {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
              <div className="mt-5 flex flex-col gap-2">
                <Dialog.Close asChild>
                  <button type="button" disabled={pending} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                    Keep subscription
                  </button>
                </Dialog.Close>
                <button type="button" onClick={confirmCancellation} disabled={pending} className="min-h-11 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60">
                  {pending ? "Cancelling…" : "Confirm cancellation"}
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
